/**
 * Legal content — Privacy Policy and Terms & Conditions drafts in four
 * languages (English, Spanish, French, Haitian Kreyòl). These are provided
 * as a professional starting point; the platform operator remains
 * responsible for reviewing and adapting them with qualified legal counsel
 * in every jurisdiction where the service is offered.
 *
 * Structure: each policy is an ordered array of sections. Each section has
 * a `title` and one or more paragraphs (`body`). The renderer walks the
 * array and applies consistent typography.
 */

const BRAND = "My GlobalTopup";
const EFFECTIVE = "February 2026";
const CONTACT_EMAIL = "support@myglobaltopup.com";

// --------------------------- English ---------------------------------- //
const en = {
  privacy: {
    heading: "Privacy Policy",
    intro: `${BRAND} ("we", "us", "our") respects the privacy of every person who interacts with the platform. This policy explains what personal data we collect, why we collect it, how we protect it, and the rights available to you.`,
    effective: `Effective date: ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Data we collect",
        body: [
          "Account data: name, email, phone number, country, role in the network (Master Agent, Agent, Collaborator), and, where applicable, identity documents used to comply with know-your-customer requirements.",
          "Transaction data: mobile topups, cash transfers, wallet fundings, receipts, timestamps, amounts and the internal identifiers linking each operation to your account.",
          "Technical data: IP address, device identifier, browser type, language preference and audit-log entries generated by security-sensitive actions such as sign-in and password resets.",
        ],
      },
      {
        title: "2. How we use your data",
        body: [
          "To deliver the service you request — routing topups, crediting wallets, generating receipts and computing commissions.",
          "To protect your account from unauthorised access with two-factor authentication, trusted-device flows and an immutable audit log.",
          "To meet our regulatory obligations, including anti-money-laundering, sanctions screening and tax reporting.",
          "To improve the platform through aggregated, non-identifying analytics.",
        ],
      },
      {
        title: "3. Sharing with third parties",
        body: [
          "We share the minimum data required to complete a transaction with our payment processor, our SMS provider (for one-time codes) and the mobile operators to which you send topups.",
          "We never sell your personal information. We may disclose data when compelled by a valid legal request in a jurisdiction where we operate.",
        ],
      },
      {
        title: "4. Data retention",
        body: [
          "Account and transaction records are retained for as long as your account is active and for the additional period required by applicable financial-services regulations, typically five to ten years.",
        ],
      },
      {
        title: "5. Your rights",
        body: [
          "Subject to local law, you may request access to your data, correction of inaccurate data, deletion of data that is no longer necessary, and portability of the data you have provided to us. Contact us at " + CONTACT_EMAIL + " to exercise these rights.",
        ],
      },
      {
        title: "6. Security",
        body: [
          "We use industry-standard encryption in transit and at rest, strict role-based access controls, and periodic penetration testing. No online service can be guaranteed 100% secure, but we treat security as an ongoing priority.",
        ],
      },
      {
        title: "7. Changes to this policy",
        body: [
          "We may update this policy from time to time. Material changes will be communicated in the app and by email at least fourteen days before they take effect.",
        ],
      },
      {
        title: "8. Contact",
        body: [
          `Questions or complaints regarding this policy can be sent to ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  terms: {
    heading: "Terms & Conditions",
    intro: `These Terms govern access to and use of the ${BRAND} platform. By creating an account or using the service you accept these Terms in full.`,
    effective: `Effective date: ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Description of the service",
        body: [
          `${BRAND} provides a reseller platform for international mobile topups, digital gift cards, bill payments, cash transfers and related services. Availability of individual services depends on the country and operator.`,
        ],
      },
      {
        title: "2. Accounts and eligibility",
        body: [
          "You must be at least eighteen years old and legally capable of entering into a binding contract. You are responsible for keeping your credentials confidential and for every action taken through your account.",
          "You agree to provide accurate identification when requested and to notify us promptly of any suspected unauthorised use of your account.",
        ],
      },
      {
        title: "3. Wallet, fees and refunds",
        body: [
          "Wallets are denominated in USD unless otherwise stated. Funds are credited to your wallet through approved payment channels.",
          "Fees and commissions applicable to each operation are shown in the platform before you confirm. All amounts are final once a topup or transfer has been dispatched successfully; refunds may be issued only when a service could not be delivered.",
        ],
      },
      {
        title: "4. Acceptable use",
        body: [
          "You must not use the platform to launder money, finance sanctioned entities, or resell services in a way that violates the terms of the underlying operators. We may suspend or terminate any account that we reasonably believe is engaged in such activity.",
        ],
      },
      {
        title: "5. Intellectual property",
        body: [
          `All rights in the platform, including software, trademarks, logos and text, are owned by ${BRAND} or its licensors. No content may be copied, reproduced or redistributed without prior written consent.`,
          "Third-party brand names shown on the platform belong to their respective owners and are used solely to identify available services.",
        ],
      },
      {
        title: "6. Availability and limitation of liability",
        body: [
          "The service is provided on an \"as is\" and \"as available\" basis. To the maximum extent permitted by law, we exclude liability for indirect, incidental or consequential damages.",
          "Our aggregate liability arising out of or in connection with the service is limited to the fees paid by the user during the six months preceding the event giving rise to the claim.",
        ],
      },
      {
        title: "7. Termination",
        body: [
          "You may close your account at any time. We may suspend or terminate your access, without prior notice, if you breach these Terms or if we are required to do so by law.",
        ],
      },
      {
        title: "8. Governing law",
        body: [
          "These Terms are governed by the laws of the jurisdiction in which the platform operator is domiciled, without regard to conflict-of-laws principles. Disputes shall be submitted to the competent courts of that jurisdiction.",
        ],
      },
      {
        title: "9. Contact",
        body: [
          `For any question regarding these Terms, please contact ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

// --------------------------- Español ---------------------------------- //
const es = {
  privacy: {
    heading: "Política de Privacidad",
    intro: `${BRAND} ("nosotros") respeta la privacidad de todas las personas que interactúan con la plataforma. Esta política explica qué datos personales recopilamos, por qué los recopilamos, cómo los protegemos y los derechos que están a tu disposición.`,
    effective: `Fecha de vigencia: ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Datos que recopilamos",
        body: [
          "Datos de cuenta: nombre, correo electrónico, teléfono, país, rol en la red (Master Agent, Agent, Collaborator) y, cuando corresponda, documentos de identidad utilizados para cumplir requisitos de conocimiento del cliente.",
          "Datos de transacciones: recargas móviles, transferencias de efectivo, recargas de cartera, comprobantes, marcas de tiempo, montos e identificadores internos que vinculan cada operación a tu cuenta.",
          "Datos técnicos: dirección IP, identificador de dispositivo, tipo de navegador, idioma y registros de auditoría generados por acciones sensibles como el inicio de sesión y el restablecimiento de contraseña.",
        ],
      },
      {
        title: "2. Cómo utilizamos tus datos",
        body: [
          "Para entregarte el servicio solicitado: enrutar recargas, acreditar carteras, generar comprobantes y calcular comisiones.",
          "Para proteger tu cuenta frente a accesos no autorizados mediante 2FA, flujos de dispositivos de confianza y un registro de auditoría inmutable.",
          "Para cumplir con nuestras obligaciones regulatorias, incluyendo prevención de lavado de dinero, listas de sanciones y reportes fiscales.",
          "Para mejorar la plataforma a través de análisis agregados y no identificativos.",
        ],
      },
      {
        title: "3. Compartir con terceros",
        body: [
          "Compartimos el mínimo dato necesario para completar una transacción con nuestro procesador de pagos, nuestro proveedor de SMS (para códigos de un solo uso) y los operadores móviles a los que envías recargas.",
          "Nunca vendemos tu información personal. Podemos divulgar datos cuando así lo exija una solicitud legal válida en una jurisdicción donde operamos.",
        ],
      },
      {
        title: "4. Conservación de datos",
        body: [
          "Los registros de cuenta y transacciones se conservan mientras tu cuenta esté activa y por el periodo adicional requerido por la normativa financiera aplicable, habitualmente entre cinco y diez años.",
        ],
      },
      {
        title: "5. Tus derechos",
        body: [
          "Sujeto a la legislación local, puedes solicitar acceso a tus datos, rectificación de datos inexactos, eliminación de datos que ya no sean necesarios y portabilidad de los datos que nos hayas proporcionado. Contáctanos en " + CONTACT_EMAIL + " para ejercer estos derechos.",
        ],
      },
      {
        title: "6. Seguridad",
        body: [
          "Utilizamos cifrado estándar de la industria tanto en tránsito como en reposo, controles de acceso estrictos por rol y pruebas de penetración periódicas. Ningún servicio en línea puede garantizarse 100% seguro, pero tratamos la seguridad como una prioridad continua.",
        ],
      },
      {
        title: "7. Cambios en esta política",
        body: [
          "Podemos actualizar esta política periódicamente. Los cambios materiales se comunicarán en la aplicación y por correo electrónico con al menos catorce días de antelación.",
        ],
      },
      {
        title: "8. Contacto",
        body: [
          `Preguntas o reclamos sobre esta política pueden enviarse a ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  terms: {
    heading: "Términos y Condiciones",
    intro: `Estos Términos rigen el acceso y uso de la plataforma ${BRAND}. Al crear una cuenta o utilizar el servicio, aceptas estos Términos en su totalidad.`,
    effective: `Fecha de vigencia: ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Descripción del servicio",
        body: [
          `${BRAND} ofrece una plataforma de reventa para recargas móviles internacionales, tarjetas de regalo digitales, pago de facturas, transferencias de efectivo y servicios relacionados. La disponibilidad de cada servicio depende del país y del operador.`,
        ],
      },
      {
        title: "2. Cuentas y elegibilidad",
        body: [
          "Debes tener al menos dieciocho años y capacidad legal para celebrar un contrato vinculante. Eres responsable de mantener la confidencialidad de tus credenciales y de toda acción realizada a través de tu cuenta.",
          "Aceptas proporcionar identificación precisa cuando se solicite y notificarnos de inmediato ante cualquier sospecha de uso no autorizado de tu cuenta.",
        ],
      },
      {
        title: "3. Cartera, tarifas y reembolsos",
        body: [
          "Las carteras están denominadas en USD salvo indicación contraria. Los fondos se acreditan a tu cartera a través de los canales de pago aprobados.",
          "Las tarifas y comisiones aplicables a cada operación se muestran en la plataforma antes de tu confirmación. Todos los montos son definitivos una vez que una recarga o transferencia se haya despachado correctamente; los reembolsos solo se emiten cuando un servicio no pudo ser entregado.",
        ],
      },
      {
        title: "4. Uso aceptable",
        body: [
          "No debes utilizar la plataforma para lavar dinero, financiar entidades sancionadas ni revender servicios de forma que viole los términos de los operadores subyacentes. Podemos suspender o cerrar cualquier cuenta que consideremos razonablemente involucrada en tales actividades.",
        ],
      },
      {
        title: "5. Propiedad intelectual",
        body: [
          `Todos los derechos sobre la plataforma, incluyendo software, marcas, logotipos y textos, pertenecen a ${BRAND} o a sus licenciantes. Ningún contenido puede copiarse, reproducirse o redistribuirse sin consentimiento previo por escrito.`,
          "Los nombres comerciales de terceros mostrados en la plataforma pertenecen a sus respectivos propietarios y se utilizan únicamente para identificar los servicios disponibles.",
        ],
      },
      {
        title: "6. Disponibilidad y limitación de responsabilidad",
        body: [
          "El servicio se ofrece \"tal cual\" y \"según disponibilidad\". En la máxima medida permitida por la ley, quedan excluidos los daños indirectos, incidentales o consecuentes.",
          "Nuestra responsabilidad agregada derivada del servicio se limita a las tarifas pagadas por el usuario durante los seis meses previos al evento que dio lugar al reclamo.",
        ],
      },
      {
        title: "7. Terminación",
        body: [
          "Puedes cerrar tu cuenta en cualquier momento. Podemos suspender o terminar tu acceso, sin previo aviso, si incumples estos Términos o si estamos obligados a hacerlo por ley.",
        ],
      },
      {
        title: "8. Legislación aplicable",
        body: [
          "Estos Términos se rigen por las leyes de la jurisdicción en la que el operador de la plataforma tiene su domicilio, sin considerar principios de conflicto de leyes. Las controversias se someterán a los tribunales competentes de dicha jurisdicción.",
        ],
      },
      {
        title: "9. Contacto",
        body: [
          `Para cualquier pregunta sobre estos Términos, contacta a ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

// --------------------------- Français --------------------------------- //
const fr = {
  privacy: {
    heading: "Politique de confidentialité",
    intro: `${BRAND} ("nous") respecte la vie privée de toutes les personnes qui interagissent avec la plateforme. Cette politique explique quelles données personnelles nous collectons, pourquoi nous les collectons, comment nous les protégeons et les droits dont vous disposez.`,
    effective: `Date d'entrée en vigueur : ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Données que nous collectons",
        body: [
          "Données de compte : nom, adresse e-mail, numéro de téléphone, pays, rôle dans le réseau (Master Agent, Agent, Collaborateur) et, le cas échéant, documents d'identité utilisés pour se conformer aux exigences de connaissance du client.",
          "Données de transaction : recharges mobiles, transferts d'espèces, approvisionnement du portefeuille, reçus, horodatages, montants et identifiants internes reliant chaque opération à votre compte.",
          "Données techniques : adresse IP, identifiant d'appareil, type de navigateur, préférence de langue et entrées du journal d'audit générées par les actions sensibles telles que la connexion et la réinitialisation du mot de passe.",
        ],
      },
      {
        title: "2. Comment nous utilisons vos données",
        body: [
          "Pour délivrer le service que vous demandez — acheminer les recharges, créditer les portefeuilles, générer les reçus et calculer les commissions.",
          "Pour protéger votre compte contre les accès non autorisés grâce à l'authentification à deux facteurs, aux flux d'appareils de confiance et à un journal d'audit immuable.",
          "Pour respecter nos obligations réglementaires, notamment en matière de lutte contre le blanchiment, de filtrage des sanctions et de déclarations fiscales.",
          "Pour améliorer la plateforme grâce à des analyses agrégées et non identifiantes.",
        ],
      },
      {
        title: "3. Partage avec des tiers",
        body: [
          "Nous partageons le minimum de données nécessaires pour finaliser une transaction avec notre prestataire de paiement, notre fournisseur SMS (pour les codes à usage unique) et les opérateurs mobiles auxquels vous envoyez des recharges.",
          "Nous ne vendons jamais vos informations personnelles. Nous pouvons divulguer des données lorsqu'une demande légale valide l'exige dans une juridiction où nous opérons.",
        ],
      },
      {
        title: "4. Conservation des données",
        body: [
          "Les enregistrements de compte et de transactions sont conservés tant que votre compte est actif et pendant la période supplémentaire exigée par la réglementation financière applicable, généralement de cinq à dix ans.",
        ],
      },
      {
        title: "5. Vos droits",
        body: [
          "Sous réserve du droit local, vous pouvez demander l'accès à vos données, la correction des données inexactes, la suppression des données devenues inutiles et la portabilité des données que vous nous avez fournies. Contactez-nous à " + CONTACT_EMAIL + " pour exercer ces droits.",
        ],
      },
      {
        title: "6. Sécurité",
        body: [
          "Nous utilisons un chiffrement standard de l'industrie en transit et au repos, des contrôles d'accès stricts basés sur les rôles et des tests d'intrusion périodiques. Aucun service en ligne ne peut être garanti à 100%, mais nous traitons la sécurité comme une priorité continue.",
        ],
      },
      {
        title: "7. Modifications de la politique",
        body: [
          "Nous pouvons mettre à jour cette politique de temps à autre. Les modifications substantielles seront communiquées dans l'application et par e-mail au moins quatorze jours avant leur entrée en vigueur.",
        ],
      },
      {
        title: "8. Contact",
        body: [
          `Toute question ou réclamation concernant cette politique peut être adressée à ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  terms: {
    heading: "Conditions générales",
    intro: `Les présentes Conditions régissent l'accès et l'utilisation de la plateforme ${BRAND}. En créant un compte ou en utilisant le service, vous acceptez ces Conditions dans leur intégralité.`,
    effective: `Date d'entrée en vigueur : ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Description du service",
        body: [
          `${BRAND} fournit une plateforme de revente pour les recharges mobiles internationales, les cartes cadeaux numériques, le paiement de factures, les transferts d'espèces et les services associés. La disponibilité de chaque service dépend du pays et de l'opérateur.`,
        ],
      },
      {
        title: "2. Comptes et éligibilité",
        body: [
          "Vous devez être âgé d'au moins dix-huit ans et avoir la capacité juridique de conclure un contrat contraignant. Vous êtes responsable de la confidentialité de vos identifiants et de toute action effectuée via votre compte.",
          "Vous acceptez de fournir une identification exacte lorsque cela vous est demandé et de nous informer sans délai de toute utilisation non autorisée présumée de votre compte.",
        ],
      },
      {
        title: "3. Portefeuille, frais et remboursements",
        body: [
          "Les portefeuilles sont libellés en USD sauf indication contraire. Les fonds sont crédités via les canaux de paiement approuvés.",
          "Les frais et commissions applicables à chaque opération sont affichés sur la plateforme avant votre confirmation. Tous les montants sont définitifs une fois qu'une recharge ou un transfert a été envoyé avec succès ; des remboursements peuvent être émis uniquement lorsqu'un service n'a pas pu être livré.",
        ],
      },
      {
        title: "4. Usage acceptable",
        body: [
          "Vous ne devez pas utiliser la plateforme pour blanchir de l'argent, financer des entités sous sanctions ou revendre des services d'une manière qui violerait les conditions des opérateurs sous-jacents. Nous pouvons suspendre ou clôturer tout compte que nous estimons raisonnablement engagé dans de telles activités.",
        ],
      },
      {
        title: "5. Propriété intellectuelle",
        body: [
          `Tous les droits sur la plateforme, y compris logiciels, marques, logos et textes, appartiennent à ${BRAND} ou à ses concédants. Aucun contenu ne peut être copié, reproduit ou redistribué sans consentement écrit préalable.`,
          "Les marques tierces affichées sur la plateforme appartiennent à leurs propriétaires respectifs et sont utilisées uniquement pour identifier les services disponibles.",
        ],
      },
      {
        title: "6. Disponibilité et limitation de responsabilité",
        body: [
          "Le service est fourni \"en l'état\" et \"selon disponibilité\". Dans la mesure maximale autorisée par la loi, nous excluons toute responsabilité pour dommages indirects, accessoires ou consécutifs.",
          "Notre responsabilité globale liée au service est limitée aux frais payés par l'utilisateur au cours des six mois précédant l'événement à l'origine de la réclamation.",
        ],
      },
      {
        title: "7. Résiliation",
        body: [
          "Vous pouvez clôturer votre compte à tout moment. Nous pouvons suspendre ou résilier votre accès, sans préavis, en cas de violation des présentes Conditions ou lorsque la loi nous y oblige.",
        ],
      },
      {
        title: "8. Droit applicable",
        body: [
          "Les présentes Conditions sont régies par les lois de la juridiction dans laquelle l'opérateur de la plateforme est domicilié, sans égard aux principes de conflits de lois. Tout litige sera soumis aux tribunaux compétents de ladite juridiction.",
        ],
      },
      {
        title: "9. Contact",
        body: [
          `Pour toute question concernant ces Conditions, veuillez contacter ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

// --------------------------- Kreyòl ayisyen --------------------------- //
const ht = {
  privacy: {
    heading: "Politik Konfidansyalite",
    intro: `${BRAND} ("nou") respekte vi prive tout moun ki entèraji ak platfòm nan. Politik sa a eksplike ki done pèsonèl nou kolekte, poukisa nou kolekte yo, kijan nou pwoteje yo, ak dwa ou genyen.`,
    effective: `Dat efektivite : ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Done nou kolekte",
        body: [
          "Done kont : non, imel, nimewo telefòn, peyi, wòl nan rezo a (Master Agent, Agent, Collaborator) ak, lè sa nesesè, dokiman idantifikasyon pou ranpli egzijans KYC.",
          "Done tranzaksyon : rechaj mobil, transfè lajan, rechaj bous, resi, tan, montan ak idantifyan entèn ki lye chak operasyon ak kont ou.",
          "Done teknik : adrès IP, idantifyan aparèy, tip navigatè, lang ak antre nan jounal odit yo ke aksyon sansib yo tankou koneksyon oswa reset modpas jenere.",
        ],
      },
      {
        title: "2. Kijan nou itilize done ou yo",
        body: [
          "Pou livre sèvis ou mande a — voye rechaj, kredite bous, jenere resi ak kalkile komisyon.",
          "Pou pwoteje kont ou kont aksè san otorizasyon ak 2FA, aparèy konfyans ak yon jounal odit ki pa ka chanje.",
          "Pou ranpli obligasyon regilatwa nou yo, tankou konba kont blanchisman lajan, kontwòl sanksyon ak deklarasyon fiskal.",
          "Pou amelyore platfòm nan atravè analiz agregate ki pa idantifye pèsonn.",
        ],
      },
      {
        title: "3. Pataje ak twazyèm pati",
        body: [
          "Nou pataje minimòm done nesesè pou konplete yon tranzaksyon ak pwosesè peman nou, founisè SMS nou (pou kòd yon sèl fwa) ak operatè mobil kote ou voye rechaj yo.",
          "Nou pa janm vann enfòmasyon pèsonèl ou. Nou ka divilge done lè yon demand legal valab egzije sa nan yon jiridiksyon kote nou opere.",
        ],
      },
      {
        title: "4. Konsèvasyon done",
        body: [
          "Dosye kont ak tranzaksyon konsève pandan kont ou aktif ak pou peryòd anplis regilasyon finansyè aplikab egzije, jeneralman senk a dis ane.",
        ],
      },
      {
        title: "5. Dwa ou yo",
        body: [
          "Selon lwa lokal la, ou ka mande aksè a done ou yo, koreksyon done enkorèk, efasman done ki pa nesesè ankò ak pòtabilite done ou te ba nou. Kontakte nou nan " + CONTACT_EMAIL + " pou egzèse dwa sa yo.",
        ],
      },
      {
        title: "6. Sekirite",
        body: [
          "Nou itilize chifreman estanda endistri a nan transpò ak nan repo, kontwòl aksè estrik pa wòl, ak tès penetrasyon peryodik. Pa gen sèvis anliy ki ka garanti 100% sekirize, men nou trete sekirite kòm yon priyorite kontini.",
        ],
      },
      {
        title: "7. Chanjman nan politik sa a",
        body: [
          "Nou ka mete politik sa a ajou tanzantan. Chanjman enpòtan yo pral kominike nan aplikasyon an ak pa imel omwen katòz jou anvan yo antre an vigè.",
        ],
      },
      {
        title: "8. Kontakte",
        body: [
          `Kesyon oswa plent konsènan politik sa a ka voye nan ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  terms: {
    heading: "Kondisyon Jeneral",
    intro: `Kondisyon sa yo gouvène aksè ak itilizasyon platfòm ${BRAND}. Lè w kreye yon kont oswa itilize sèvis la, ou aksepte Kondisyon sa yo an antye.`,
    effective: `Dat efektivite : ${EFFECTIVE}`,
    sections: [
      {
        title: "1. Deskripsyon sèvis la",
        body: [
          `${BRAND} bay yon platfòm revant pou rechaj mobil entènasyonal, kat kado dijital, peman fakti, transfè lajan ak sèvis ki gen rapò. Disponiblite chak sèvis depann de peyi a ak operatè a.`,
        ],
      },
      {
        title: "2. Kont ak kalifikasyon",
        body: [
          "Ou dwe gen omwen dizuit ane ak kapasite legal pou antre nan yon kontra obligatwa. Ou responsab pou kenbe idantifyan ou konfidansyèl ak pou chak aksyon ki fèt atravè kont ou.",
          "Ou dakò pou bay idantifikasyon egzak lè yo mande sa ak pou avize nou tousuit sou nenpòt itilizasyon san otorizasyon ke ou sispèk.",
        ],
      },
      {
        title: "3. Bous, frè ak ranbousman",
        body: [
          "Bous yo nan USD sof endikasyon kontrè. Fon yo kredite nan bous ou nan chanèl peman ki apwouve.",
          "Frè ak komisyon aplikab pou chak operasyon yo montre nan platfòm nan anvan konfimasyon ou. Tout montan definitif yon fwa yon rechaj oswa transfè yo voye avèk siksè ; ranbousman yo bay sèlman lè yon sèvis pa te ka livre.",
        ],
      },
      {
        title: "4. Itilizasyon akseptab",
        body: [
          "Ou pa dwe itilize platfòm nan pou blanchi lajan, finanse antite ki gen sanksyon oswa revann sèvis nan yon fason ki vyole kondisyon operatè yo. Nou ka sispann oswa fèmen nenpòt kont ke nou kwè rezonabman patisipe nan aktivite sa yo.",
        ],
      },
      {
        title: "5. Pwopriyete entelektyèl",
        body: [
          `Tout dwa sou platfòm nan, tankou lojisyèl, mak, logo ak tèks, se pou ${BRAND} oswa lisansye li yo. Okenn kontni pa ka kopye, repwodwi oswa redistribye san konsantman ekri davans.`,
          "Non komèsyal twazyèm pati ki montre nan platfòm nan se pwopriyete pwopriyetè yo ak yo itilize sèlman pou idantifye sèvis disponib.",
        ],
      },
      {
        title: "6. Disponiblite ak limitasyon responsablite",
        body: [
          "Sèvis la bay \"jan li ye\" ak \"jan li disponib\". Nan maksimòm ke lwa a pèmèt, nou eskli responsablite pou domaj endirèk, ensidan oswa konsekan.",
          "Responsablite total nou ki soti nan sèvis la limite a frè yo peye pa itilizatè a pandan sis mwa anvan evènman ki bay reklamasyon an.",
        ],
      },
      {
        title: "7. Rezilyasyon",
        body: [
          "Ou ka fèmen kont ou nenpòt lè. Nou ka sispann oswa mete fen nan aksè ou, san avèti, si w vyole Kondisyon sa yo oswa si lwa a egzije nou fè sa.",
        ],
      },
      {
        title: "8. Lwa aplikab",
        body: [
          "Kondisyon sa yo gouvène pa lwa jiridiksyon kote operatè platfòm nan domisilye, san konsiderasyon prensip konfli lwa. Diskisyon yo pral soumèt nan tribinal konpetan jiridiksyon sa a.",
        ],
      },
      {
        title: "9. Kontakte",
        body: [
          `Pou nenpòt kesyon sou Kondisyon sa yo, tanpri kontakte ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

const LEGAL_CONTENT = { en, es, fr, ht };

export function getLegalContent(lang, kind /* "privacy" | "terms" */) {
  const bag = LEGAL_CONTENT[lang] || LEGAL_CONTENT.en;
  return bag[kind] || LEGAL_CONTENT.en[kind];
}

export default LEGAL_CONTENT;
